import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { usePackages, BASE_PRICES } from '../contexts/PackagesContext';
import { Crown } from 'lucide-react';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-26423">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const {
    user
  } = useUser();
  const {
    getPackagePrice
  } = usePackages();
  // Subscription discount for astrologers
  const isAstrologer = user?.role === 'astrologer';
  const currentPlan = isAstrologer ? 'PRO' : 'FREE'; // In real app, from SubscriptionContext
  const subDiscount = currentPlan === 'TOP' ? 20 : currentPlan === 'PRO' ? 10 : 0;
  const getDiscountedPackagePrice = (basePrice: number, type: 20 | 50 | 100) => {
    if (subDiscount > 0) {
      return getPackagePrice(type, subDiscount);
    }
    return basePrice;
  };
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }, ...(subDiscount > 0 ? [{
        label: `Скидка ${subDiscount}% (${currentPlan})` as string,
        variant: 'pro' as const
      }] : [])]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-26424">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-26425">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-26426" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-26427" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-26428" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-26429">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-26430" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-26431">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-26432">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-26433">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-26434">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-26435">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-26436">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-26437" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-26438">
                <div className="flex items-center" data-id="element-26439">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-26440" />
                  <span data-id="element-26441">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-26442">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-26443" />
                  <span data-id="element-26444">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-26445">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-26446" />
                  <span data-id="element-26447">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-26448">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26449">
          <div className="text-center mb-10" data-id="element-26450">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-26451">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-26452">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-26453">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-26454" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-26455" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-26456">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-26457" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-26458" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-26459" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-26460">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-26461">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-26462">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-26463">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-26464">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-26465">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-26466">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-26467">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26468">
            <div className="text-center mb-10" data-id="element-26469">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-26470">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-26471">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-26472">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-26473">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-26474">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-26475" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-26476">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-26477">
                      <Moon className="w-5 h-5" data-id="element-26478" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-26479">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26480">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-26481">
                    <div className="text-xs text-slate-500" data-id="element-26482">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-26483">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26484">
                    <div className="flex items-center justify-between mb-2" data-id="element-26485">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26486">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-26487">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-26488">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-26489">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26490" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26491" />
                  <div className="mb-5 flex-grow" data-id="element-26492">
                    <div className="flex items-center justify-between mb-2" data-id="element-26493">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-26494">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-26495">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-26496">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26497">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-26498" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26499">
                    <Button fullWidth onClick={() => {
                    addItem({
                      id: `premium-natal-${Date.now()}`,
                      name: 'Premium отчёт: Натальная карта',
                      description: 'Полный профессиональный отчёт с глубоким анализом.',
                      price: 3900,
                      type: 'premium-report',
                      serviceName: 'Натальная карта',
                      format: 'PDF-отчёт',
                      duration: '30-40 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-26500">
                      Купить за 3 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-natal')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26501">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26502" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-26503">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-26504">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-26505" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-26506">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-26507">
                      <Sun className="w-5 h-5" data-id="element-26508" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-26509">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26510">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-26511">
                    <div className="text-xs text-slate-500" data-id="element-26512">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-26513">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26514">
                    <div className="flex items-center justify-between mb-2" data-id="element-26515">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26516">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-26517">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-26518">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-26519">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26520" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26521" />
                  <div className="mb-5 flex-grow" data-id="element-26522">
                    <div className="flex items-center justify-between mb-2" data-id="element-26523">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-26524">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-26525">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-26526">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26527">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-26528" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26529">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-solar-${Date.now()}`,
                      name: 'Premium отчёт: Соляр',
                      description: 'Полный астрологический прогноз на личный год.',
                      price: 4400,
                      type: 'premium-report',
                      serviceName: 'Соляр',
                      format: 'PDF-отчёт',
                      duration: '25-35 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-26530">
                      Купить за 4 400 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-solar')} className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26531">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26532" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-26533">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-26534">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-26535" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-26536">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-26537">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-26538">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-26539">
                      <Layers className="w-5 h-5" data-id="element-26540" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-26541">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26542">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-26543">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-26544">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-26545">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-26546">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-26547">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26548">
                    <div className="flex items-center justify-between mb-2" data-id="element-26549">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26550">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-26551">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-26552">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-26553">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26554" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26555" />
                  <div className="mb-5 flex-grow" data-id="element-26556">
                    <div className="flex items-center justify-between mb-2" data-id="element-26557">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-26558">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-26559">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-26560">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26561">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-26562" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26563">
                    <Button variant="primary" fullWidth onClick={() => {
                    addItem({
                      id: `premium-combo-${Date.now()}`,
                      name: 'Premium отчёт: Комбо (Натал + Соляр)',
                      description: 'Полный анализ личности и прогноз на ближайший год.',
                      price: 6900,
                      type: 'premium-report',
                      serviceName: 'Комбо: Натал + Соляр',
                      format: 'PDF-отчёт',
                      duration: '50-60 страниц',
                      oldPrice: 8300,
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} className="bg-violet-600 hover:bg-violet-700" data-id="element-26564">
                      Купить за 6 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-combo')} className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26565">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26566" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-26567">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-26568">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-26569" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-26570">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-26571">
                      <Heart className="w-5 h-5" data-id="element-26572" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-26573">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26574">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-26575">
                    <div className="text-xs text-slate-500" data-id="element-26576">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-26577">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26578">
                    <div className="flex items-center justify-between mb-2" data-id="element-26579">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26580">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-26581">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-26582">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-26583">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26584" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26585" />
                  <div className="mb-5 flex-grow" data-id="element-26586">
                    <div className="flex items-center justify-between mb-2" data-id="element-26587">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-26588">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-26589">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-26590">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26591">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26592" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26593">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-compatibility-${Date.now()}`,
                      name: 'Premium отчёт: Совместимость',
                      description: 'Полный анализ совместимости двух людей.',
                      price: 4900,
                      type: 'premium-report',
                      serviceName: 'Совместимость',
                      format: 'PDF-отчёт',
                      duration: '30-40 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-26594">
                      Купить за 4 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-compatibility')} className="text-sm text-rose-500 hover:text-rose-600 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26595">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26596" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-26597">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-26598">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-26599" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-26600">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-26601">
                      <Activity className="w-5 h-5" data-id="element-26602" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-26603">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26604">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-26605">
                    <div className="text-xs text-slate-500" data-id="element-26606">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-26607">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26608">
                    <div className="flex items-center justify-between mb-2" data-id="element-26609">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26610">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-26611">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-26612">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-26613">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-26614" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26615" />
                  <div className="mb-5 flex-grow" data-id="element-26616">
                    <div className="flex items-center justify-between mb-2" data-id="element-26617">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-26618">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-26619">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-26620">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26621">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-26622" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26623">
                    <Button variant="outline" fullWidth onClick={() => {
                    addItem({
                      id: `premium-forecast-${Date.now()}`,
                      name: 'Premium отчёт: Прогноз (Транзиты)',
                      description: 'Полный астрологический прогноз на ближайшие месяцы.',
                      price: 3900,
                      type: 'premium-report',
                      serviceName: 'Прогноз (Транзиты)',
                      format: 'PDF-отчёт',
                      duration: '20-30 страниц',
                      badges: [{
                        label: 'Premium',
                        variant: 'pro'
                      }]
                    });
                    onNavigate('checkout');
                  }} data-id="element-26624">
                      Купить за 3 900 ₽
                    </Button>
                    <button onClick={() => onNavigate('service-forecast')} className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26625">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26626" />
                    </button>
                  </div>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-26627">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-26628">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-26629" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-26630">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-26631">
                      <Video className="w-5 h-5" data-id="element-26632" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-26633">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-26634">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-26635">
                    <div className="text-xs text-slate-500" data-id="element-26636">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-26637">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-26638">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-26639">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-26640">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-26641" />
                  <div className="mb-5 flex-grow" data-id="element-26642">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-26643">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-26644">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-26645">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-26646" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto" data-id="element-26647">
                    <Button variant="outline" fullWidth onClick={() => onNavigate('astrologers')} data-id="element-26648">
                      Выбрать астролога
                    </Button>
                    <button onClick={() => onNavigate('service-consultation')} className="text-sm text-sky-600 hover:text-sky-700 font-medium flex items-center justify-center gap-1 py-1" data-id="element-26649">
                      Подробнее <ChevronRight className="w-3.5 h-3.5" data-id="element-26650" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 4.5 ПАКЕТЫ ОТЧЕТОВ */}
      <AnimatedSection className="py-10 sm:py-14 lg:py-16 bg-slate-50 border-t border-slate-200" data-id="element-26651">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26652">
          <div className="text-center mb-8 sm:mb-12" data-id="element-26653">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-3 sm:mb-4" data-id="element-26654">
              <Package className="w-4 h-4 mr-1.5" data-id="element-26655" />
              Пакеты отчетов
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-26656">
              Экономьте до 30% при покупке пакета
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-26657">
              Оптимальное решение для регулярного использования. Покупайте оптом
              — платите меньше за каждый отчёт.
            </p>
          </div>

          {subDiscount > 0 && <div className="max-w-3xl mx-auto mb-6 sm:mb-8 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4" data-id="element-26658">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-26659">
                <Crown className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-26660" />
                <div className="text-sm sm:text-base" data-id="element-26661">
                  <span className="font-bold text-slate-900" data-id="element-26662">
                    Ваш тариф: {currentPlan}
                  </span>
                  <span className="text-sm text-slate-600 ml-1 sm:ml-2" data-id="element-26663">
                    — скидка{' '}
                    <span className="font-bold text-indigo-600" data-id="element-26664">
                      {subDiscount}%
                    </span>{' '}
                    на все пакеты
                  </span>
                </div>
              </div>
            </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto" data-id="element-26665">
            {/* Пакет 20 */}
            <Card padding="md" className="flex flex-col border-slate-200 hover:shadow-lg transition-shadow sm:last:col-span-2 md:last:col-span-1" data-id="element-26666">
              <div className="mb-4 sm:mb-6" data-id="element-26667">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2" data-id="element-26668">
                  20 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6" data-id="element-26669">
                  Оптимально для регулярного использования
                </p>
                <div className="mb-2" data-id="element-26670">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-slate-400 line-through" data-id="element-26671">
                      76 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-26672">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-26673">
                      {getDiscountedPackagePrice(76000, 20).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-26674">
                      <Crown className="w-3 h-3" data-id="element-26675" />-{subDiscount}% (
                      {(76000 - getDiscountedPackagePrice(76000, 20)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-26676">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-26677">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-26678">
                    <span className="text-slate-500" data-id="element-26679">За 1 отчет:</span>
                    <span className="font-semibold text-slate-900" data-id="element-26680">
                      {Math.round(getDiscountedPackagePrice(76000, 20) / 20).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-26681">
                    <span className="text-slate-500" data-id="element-26682">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-26683">
                      {(90000 - getDiscountedPackagePrice(76000, 20)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-3" data-id="element-26684">
                  {['Любые отчеты на выбор', 'Использование в любое время', 'Доступ без ограничений по времени'].map((f, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-26685">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-26686" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="outline" fullWidth size="lg" onClick={() => handleBuyPackage({
              title: 'Пакет 20 отчетов',
              desc: 'Оптимально для регулярного использования',
              price: getDiscountedPackagePrice(76000, 20),
              oldPrice: subDiscount > 0 ? 76000 : undefined
            })} data-id="element-26687">
                Купить пакет
              </Button>
            </Card>

            {/* Пакет 50 */}
            <Card padding="md" className="flex flex-col border-indigo-200 shadow-xl shadow-indigo-500/10 relative sm:col-span-2 md:col-span-1 md:-translate-y-4" data-id="element-26688">
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10" data-id="element-26689">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg flex items-center gap-1 sm:gap-1.5 whitespace-nowrap" data-id="element-26690">
                  <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-26691" />
                  Популярно
                </span>
              </div>

              <div className="mb-4 sm:mb-6 pt-2" data-id="element-26692">
                <h3 className="text-xl sm:text-2xl font-bold text-indigo-900 mb-1 sm:mb-2" data-id="element-26693">
                  50 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-indigo-600/80 mb-4 sm:mb-6" data-id="element-26694">
                  Лучший выбор для активных пользователей
                </p>
                <div className="mb-2" data-id="element-26695">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-indigo-300 line-through" data-id="element-26696">
                      180 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-26697">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-indigo-900" data-id="element-26698">
                      {getDiscountedPackagePrice(180000, 50).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-26699">
                      <Crown className="w-3 h-3" data-id="element-26700" />-{subDiscount}% (
                      {(180000 - getDiscountedPackagePrice(180000, 50)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-26701">
                <div className="bg-indigo-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-26702">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-26703">
                    <span className="text-indigo-600/80" data-id="element-26704">За 1 отчет:</span>
                    <span className="font-semibold text-indigo-900" data-id="element-26705">
                      {Math.round(getDiscountedPackagePrice(180000, 50) / 50).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-26706">
                    <span className="text-indigo-600/80" data-id="element-26707">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-26708">
                      {(225000 - getDiscountedPackagePrice(180000, 50)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-3" data-id="element-26709">
                  {['Любые отчеты', 'Быстрое масштабирование работы', 'Приоритетная обработка'].map((f, i) => <li key={i} className="flex items-start text-sm text-slate-700" data-id="element-26710">
                      <Check className="w-4 h-4 text-indigo-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-26711" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="primary" fullWidth size="lg" className="shadow-md shadow-indigo-500/20" onClick={() => handleBuyPackage({
              title: 'Пакет 50 отчетов',
              desc: 'Лучший выбор для активных пользователей',
              price: getDiscountedPackagePrice(180000, 50),
              oldPrice: subDiscount > 0 ? 180000 : undefined
            })} data-id="element-26712">
                Купить пакет
              </Button>
            </Card>

            {/* Пакет 100 */}
            <Card padding="md" className="flex flex-col border-slate-200 hover:shadow-lg transition-shadow" data-id="element-26713">
              <div className="mb-4 sm:mb-6" data-id="element-26714">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2" data-id="element-26715">
                  100 отчетов
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6" data-id="element-26716">
                  Максимальная выгода для профессионалов
                </p>
                <div className="mb-2" data-id="element-26717">
                  {subDiscount > 0 && <div className="text-base sm:text-lg text-slate-400 line-through" data-id="element-26718">
                      315 000 ₽
                    </div>}
                  <div className="flex items-baseline gap-2" data-id="element-26719">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-id="element-26720">
                      {getDiscountedPackagePrice(315000, 100).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  {subDiscount > 0 && <div className="inline-flex items-center gap-1 mt-1.5 sm:mt-2 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold" data-id="element-26721">
                      <Crown className="w-3 h-3" data-id="element-26722" />-{subDiscount}% (
                      {(315000 - getDiscountedPackagePrice(315000, 100)).toLocaleString('ru-RU')}{' '}
                      ₽)
                    </div>}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-26723">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 space-y-2" data-id="element-26724">
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-26725">
                    <span className="text-slate-500" data-id="element-26726">За 1 отчет:</span>
                    <span className="font-semibold text-slate-900" data-id="element-26727">
                      {Math.round(getDiscountedPackagePrice(315000, 100) / 100).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm" data-id="element-26728">
                    <span className="text-slate-500" data-id="element-26729">Экономия:</span>
                    <span className="font-bold text-emerald-600" data-id="element-26730">
                      {(450000 - getDiscountedPackagePrice(315000, 100)).toLocaleString('ru-RU')}{' '}
                      ₽
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 sm:space-y-3" data-id="element-26731">
                  {['Все типы отчетов', 'Максимальная экономия', 'Подходит для бизнеса'].map((f, i) => <li key={i} className="flex items-start text-xs sm:text-sm text-slate-700" data-id="element-26732">
                      <Check className="w-4 h-4 text-emerald-500 mr-2 sm:mr-2.5 mt-0.5 flex-shrink-0" data-id="element-26733" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <Button variant="outline" fullWidth size="lg" onClick={() => handleBuyPackage({
              title: 'Пакет 100 отчетов',
              desc: 'Максимальная выгода для профессионалов',
              price: getDiscountedPackagePrice(315000, 100),
              oldPrice: subDiscount > 0 ? 315000 : undefined
            })} data-id="element-26734">
                Купить пакет
              </Button>
            </Card>
          </div>

          <div className="text-center mt-6 sm:mt-10" data-id="element-26735">
            <p className="text-xs sm:text-sm text-slate-500 font-medium bg-white inline-block px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-26736">
              Отчеты из пакета списываются автоматически при покупке услуг
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-6 sm:py-8 bg-white border-y border-slate-100" data-id="element-26737">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26738">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-id="element-26739">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-26740" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-26741" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-26742" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-26743" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-26744">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-26745">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-26746">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-26747">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-26748">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26749">
          <div className="text-center mb-8" data-id="element-26750">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-26751">
              <HelpCircle className="w-5 h-5" data-id="element-26752" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-26753">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-26754">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-26755">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-26756" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-26757" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-26758" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-26759" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-26760" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-26761" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-26762">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-26763">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-26764">
                  <div className="text-sm font-bold text-slate-900" data-id="element-26765">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-26766">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-26767" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-26768">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26769">
          <div className="text-center mb-8" data-id="element-26770">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-26771">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-26772" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-10 sm:py-14 lg:py-16 bg-slate-900 relative overflow-hidden" data-id="element-26773">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-26774" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-26775">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-26776">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-26777" />
          </motion.div>
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-white mb-3 sm:mb-4" data-id="element-26778">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-sm sm:text-base text-indigo-100/80 mb-6 sm:mb-8 max-w-xl mx-auto" data-id="element-26779">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-26780">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-26781" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-26782">
            <span className="flex items-center" data-id="element-26783">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-26784" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-26785">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-26786" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}